@let emailId = `${id}-recipient-email`;
@let retentionHintId = `${id}-retention-hint`;

<qpx-dialog-header
    title="Transfer building ownership"
    [showCloseButton]="!isProcessing()"
    (dismiss)="onCancel()"
/>

<qpx-dialog-content [isProcessing]="isProcessing()" contentClass="gap-4">
    <section
        class="rounded-lg border border-destructive-decorative bg-destructive-muted p-4 text-foreground"
        aria-labelledby="transfer-building-risk-title"
    >
        <div class="flex items-center gap-2">
            <div
                class="icon-container self-start mt-1 flex items-center justify-center px-2 py-1"
            >
                <ng-icon qpx context="destructive" name="lucideTriangleAlert" />
            </div>
            <div class="min-w-0">
                <h2
                    id="transfer-building-risk-title"
                    qpxBody="destructive"
                    tone="muted"
                    variant="lg"
                    class="font-semibold mb-1"
                >
                    High-risk action
                </h2>
                <p qpxBody="destructive" tone="muted">
                    A transfer can only be sent to an owner of another
                    organisation. The recipient must accept the transfer for
                    ownership to change. Once accepted, your organisation will
                    lose access unless you choose to retain it for
                    <span class="font-semibold">{{ buildingName() }}</span
                    >.
                </p>
            </div>
        </div>
    </section>

    <form class="contents" novalidate (submit)="$event.preventDefault()">
        <div>
            <label mat-label [for]="emailId">Recipient email address</label>
            <mat-form-field qpxInput subscriptSizing="dynamic" class="w-full">
                <input
                    #emailInput
                    matInput
                    qpxEmail
                    [id]="emailId"
                    [formField]="form.recipientEmail"
                    autocomplete="email"
                />

                @if (form.recipientEmail().getError('required')) {
                    <mat-error>Email is required</mat-error>
                } @else if (form.recipientEmail().getError('email')) {
                    <mat-error>Please enter a valid email address</mat-error>
                }
            </mat-form-field>

            @if (recipientPreviewState() !== 'idle') {
                <output
                    class="mt-2 flex min-w-0 items-center gap-3 text-sm text-muted"
                    aria-live="polite"
                    aria-atomic="true"
                >
                    @if (recipientPreviewState() === 'loading') {
                        <qpx-loader
                            size="sm"
                            containerClass="shrink-0"
                            ariaLabel="Checking organisation"
                        />
                        <span>Checking organisation…</span>
                    } @else {
                        <span
                            class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted"
                            aria-hidden="true"
                        >
                            <ng-icon
                                qpx
                                [name]="
                                    recipientPreviewState() === 'failure'
                                        ? 'lucideCircleAlert'
                                        : 'lucideBriefcaseBusiness'
                                "
                            />
                        </span>

                        @if (recipientPreviewState() === 'recognised') {
                            <span class="min-w-0 flex-1">
                                <span
                                    qpxCell="secondary"
                                    tone="muted"
                                    variant="sm"
                                    class="block"
                                >
                                    Recipient organisation
                                </span>
                                <span
                                    qpxCell
                                    variant="md"
                                    qpxOverflow
                                    #overflow="qpxOverflow"
                                    class="block truncate font-semibold"
                                    [qpxTooltip]="
                                        recipientPreview()?.organisationName ??
                                        ''
                                    "
                                    [matTooltipDisabled]="
                                        !overflow.isOverflowing()
                                    "
                                    [attr.tabindex]="
                                        overflow.isOverflowing() ? 0 : null
                                    "
                                >
                                    {{ recipientPreview()?.organisationName }}
                                </span>
                            </span>
                        } @else if (recipientPreviewState() === 'unknown') {
                            <span>No organisation found for this email</span>
                        } @else if (recipientPreviewState() === 'unavailable') {
                            <span>Organisation unavailable</span>
                        } @else {
                            <span class="min-w-0">
                                Couldn't check organisation
                                <button
                                    qpxButton
                                    matButton="text"
                                    size="sm"
                                    (click)="retryRecipientPreview()"
                                >
                                    <span>Retry</span>
                                </button>
                            </span>
                        }
                    }
                </output>
            }
        </div>
        <div>
            <mat-checkbox
                qpx
                [id]="`${id}-retain-access`"
                [formField]="form.retainOriginalOwnerAccess"
                [aria-describedby]="retentionHintId"
            >
                Retain my organisation's access after transfer
            </mat-checkbox>
            <p
                qpxBody="secondary"
                tone="muted"
                class="mt-2"
                [id]="retentionHintId"
            >
                If selected, your organisation will retain Building Agent access
                after acceptance. Building Agents can edit building content, but
                cannot transfer ownership, change building access, or update
                document privacy. The new owner can revoke this access.
            </p>
        </div>
    </form>
</qpx-dialog-content>

<qpx-dialog-footer
    [confirmButton]="{
        label: 'Start transfer',
        icon: 'lucideArrowRightLeft',
        theme: 'primary',
    }"
    [confirmDisabled]="isProcessing() || recipientPreviewState() === 'loading'"
    [isProcessing]="isProcessing()"
    (dismiss)="onCancel()"
    (confirm)="onConfirm()"
/>
